import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, ActionIcon, Menu } from '@mantine/core'
import { IoEllipsisHorizontal } from 'react-icons/io5'
import {
  PostCard as PlatformPostCard,
  type PostCardLinkComponentProps,
  type PostCardTagDto
} from '@gunslop/ui'
import AmmoScoreBadge from './AmmoScoreBadge'
import { useStore } from '../../stores/store'
import { toast } from 'react-toastify'
import agent from '../../api/agent'
import { FlagTargetType } from '../../models/flag'

export type { PostCardTagDto as TagDto }
export {
  POST_TYPE_LABELS,
  POST_TYPE_BADGE_COLORS as POST_TYPE_COLORS,
  POST_TYPE_TOKEN_COLORS,
  BASE_POST_TYPE_OPTIONS,
  STAFF_POST_TYPE_OPTION,
  BLOG_POST_TYPE_FILTER_OPTIONS,
  PostTypeBadge
} from '@gunslop/ui'

export interface PostCardProps {
  id: string;
  title: string;
  excerpt?: string;
  type: number;
  coverImageUrl?: string | null;
  mainPhotoUrl?: string | null;
  authorUsername: string;
  authorDisplayName: string;
  authorImage?: string | null;
  authorTier?: string;
  authorScore?: number;
  voteScore: number;
  currentUserVote?: number | null;
  commentCount?: number;
  createdAt: string;
  tags?: PostCardTagDto[];
  rating?: number | null;
  variant?: 'default' | 'compact' | 'featured';
}

function RouterPostLink ({ href, children, onClick, sx }: PostCardLinkComponentProps) {
  return (
    <Box
      component={Link}
      to={href}
      onClick={onClick}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        '&:visited': { color: 'inherit' },
        ...sx
      }}
    >
      {children}
    </Box>
  )
}

export default function PostCard (props: PostCardProps) {
  const {
    id,
    authorTier = 'Newcomer',
    authorScore = 0,
    voteScore: initialVoteScore,
    currentUserVote = null,
    ...rest
  } = props

  const { voteStore, userStore } = useStore()
  const [displayScore, setDisplayScore] = useState(initialVoteScore)
  const [displayVote, setDisplayVote] = useState<number | null>(currentUserVote ?? null)

  const handleVote = async (value: 1 | -1) => {
    const prevScore = displayScore
    const prevVote = displayVote
    const delta = value === displayVote ? -value : value - (displayVote ?? 0)
    setDisplayScore((s) => s + delta)
    setDisplayVote(value === displayVote ? null : value)
    try {
      if (value === displayVote) {
        await voteStore.removeVote(0, id)
      } else {
        await voteStore.castVote(0, id, value)
      }
    } catch {
      setDisplayScore(prevScore)
      setDisplayVote(prevVote)
    }
  }

  const submitReport = async (reason: string) => {
    const r = reason.trim()
    if (!r) {
      toast.error('Please add a reason.')
      return
    }
    try {
      await agent.Flags.submit({ targetType: FlagTargetType.Post, targetId: id, reason: r })
      toast.success('Report submitted.')
    } catch {
      /* interceptor */
    }
  }

  const actionsMenu = (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon size="sm" variant="filled" sx={{ background: 'var(--gs-surface2)', color: 'var(--gs-text)' }} aria-label="Post actions">
          <IoEllipsisHorizontal size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          onClick={() => {
            const reason = window.prompt('Describe the issue for moderators:')
            if (reason) void submitReport(reason)
          }}
        >
          Report post
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )

  return (
    <PlatformPostCard
      {...rest}
      id={id}
      type={rest.type as import('@gunslop/ui').PostType}
      postHref={`/blog/${id}`}
      commentsHref={`/blog/${id}#comments`}
      voteScore={displayScore}
      currentUserVote={displayVote}
      onVote={handleVote}
      authorBadge={<AmmoScoreBadge tier={authorTier} score={authorScore} size="sm" />}
      showActionsMenu={userStore.isLoggedIn}
      actionsMenu={actionsMenu}
      LinkComponent={RouterPostLink}
    />
  )
}
